import Autoplay from 'embla-carousel-autoplay'
import { ArrowLeft, ArrowRight, Star } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'
import { PiGitFork } from 'react-icons/pi'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { feedbacks as defaultFeedbacks, FeedbackType } from '@/data/feedback'

import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Carousel, CarouselApi, CarouselContent, CarouselItem, useCarousel } from '@/components/ui/carousel'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface ProjectItem {
  id?: string | number
  name: string
  image: string
  href: string
  stars?: number
  forks?: number
}

interface ProjectSectionProps {
  className?: string
  /** Section heading (supports HTML) */
  heading?: string
  /** Section description (supports HTML) */
  description?: string
  /** Heading text color */
  headingColor?: string
  /** Description text color */
  descriptionColor?: string
  /** Background color */
  bgColor?: string
  /** Data source: 'api' or 'custom' */
  dataSource?: 'api' | 'custom'
  /** Number of items to show (API mode) */
  pageSize?: number
  /** Custom projects (custom mode) */
  projects?: ProjectItem[]
  /** Carousel card size */
  cardSize?: 'default' | 'large' | 'full'
  /** Enable autoplay */
  autoplay?: boolean
  /** Autoplay delay in ms */
  autoplayDelay?: number
}

const defaultProjectItems: ProjectItem[] = [
  {
    id: 1,
    name: 'Continuous deployment to GKE cluster',
    image: '/images/architecture/1-nguyen_tran_dang_duong.png',
    href: 'https://github.com/duongngyn0510/continuous-deployment-to-gke-cluster',
    stars: 91,
    forks: 18,
  },
  {
    id: 2,
    name: 'Building a Feature Store System',
    image: '/images/architecture/2-dung_hoang.png',
    href: 'https://github.com/dunghoang369/feature-store',
    stars: 56,
    forks: 10,
  },
  {
    id: 3,
    name: 'Building a Diabetes Prediction System',
    image: '/images/architecture/3-tien_nguyen.png',
    href: 'https://github.com/TienNguyenKha/Diabetes-Prediction',
    stars: 22,
    forks: 3,
  },
]

// Element schema for Puck editor
export const projectSectionSchema: SectionElementSchema = {
  componentType: 'ProjectSection',
  label: 'Project Section',
  category: 'content',
  description: 'Student project showcase with carousel',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main heading (supports HTML)',
    },
    {
      key: 'description',
      type: 'textarea',
      label: 'Section Description',
      helpText: 'Subheading text (supports HTML)',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'descriptionColor',
      type: 'color',
      label: 'Description Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      default: 'transparent',
    },
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      options: [
        { label: 'Default (Built-in)', value: 'api' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'api',
    },
    {
      key: 'pageSize',
      type: 'select',
      label: 'Number of Projects',
      helpText: 'Maximum items to display (default mode)',
      options: [
        { label: '3 Projects', value: 3 },
        { label: '6 Projects', value: 6 },
        { label: '9 Projects', value: 9 },
        { label: '12 Projects', value: 12 },
      ],
      default: 6,
    },
    {
      key: 'projects',
      type: 'array',
      label: 'Custom Projects',
      helpText: 'Add, edit, or remove project cards (only when Data Source is "Custom")',
      default: defaultProjectItems,
      children: [
        { key: 'name', type: 'text', label: 'Project Name', required: true },
        { key: 'image', type: 'image', label: 'Project Image URL', required: true },
        { key: 'href', type: 'text', label: 'Project URL', required: true },
        { key: 'stars', type: 'number', label: 'Stars Count' },
        { key: 'forks', type: 'number', label: 'Forks Count' },
      ],
    },
    {
      key: 'cardSize',
      type: 'select',
      label: 'Card Size',
      helpText: 'Width of each carousel card',
      options: [
        { label: 'Default (70%)', value: 'default' },
        { label: 'Large (80%)', value: 'large' },
        { label: 'Full Width', value: 'full' },
      ],
      default: 'default',
    },
    {
      key: 'autoplay',
      type: 'boolean',
      label: 'Enable Autoplay',
      default: false,
    },
    {
      key: 'autoplayDelay',
      type: 'select',
      label: 'Autoplay Speed',
      options: [
        { label: '2 seconds', value: 2000 },
        { label: '3 seconds', value: 3000 },
        { label: '5 seconds', value: 5000 },
      ],
      default: 3000,
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(projectSectionSchema)

function CardProjectStudent({ project, isCenter }: { project: FeedbackType | ProjectItem; isCenter?: boolean }) {
  const isFeedbackType = (item: FeedbackType | ProjectItem): item is FeedbackType => 'repository' in item

  const name = isFeedbackType(project) ? project.repository.name : project.name
  const image = isFeedbackType(project) ? project.repository.image : project.image
  const href = isFeedbackType(project) ? project.repository.href : project.href
  const stars = isFeedbackType(project) ? project.repository.start : project.stars || 0
  const forks = isFeedbackType(project) ? project.repository.fork : project.forks || 0

  // Detect if image is a plain URL (starts with / or http) vs a Tailwind bg class
  const isUrl = image && (image.startsWith('/') || image.startsWith('http'))

  return (
    <div
      className={cn(
        'flex h-full w-full transition-all duration-300 group',
        isCenter ? 'scale-100 opacity-100' : 'scale-[0.9] opacity-50'
      )}
    >
      <Card className="w-full overflow-hidden border shadow-lg rounded-2xl">
        <div
          className={cn(
            'aspect-[16/9] relative overflow-hidden bg-center bg-cover bg-no-repeat rounded-t-2xl',
            !isUrl && image
          )}
          style={isUrl ? { backgroundImage: `url('${image}')` } : undefined}
        >
          <Link href={href} target="_blank" className="w-full h-full flex flex-col justify-end">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            <CardContent className="relative p-8 z-20 text-white flex flex-col justify-end space-y-4">
              <div className="flex flex-col gap-3">
                <h3 className="text-2xl sm:text-3xl font-bold line-clamp-2 font-sans">{name}</h3>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Star className="size-5" />
                    <span className="text-sm font-medium">{stars}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PiGitFork className="size-5" />
                    <span className="text-sm font-medium">{forks}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Link>
        </div>
      </Card>
    </div>
  )
}

function CarouselPagination({
  api,
  activeIndex,
  total,
}: {
  api: CarouselApi | undefined
  activeIndex: number
  total: number
}) {
  const { scrollNext, canScrollNext, canScrollPrev, scrollPrev } = useCarousel()
  return (
    <div className="mt-8 lg:mt-12 flex justify-center items-center gap-4">
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'size-10 rounded-full border bg-white/80 backdrop-blur-sm transition-all duration-300',
          canScrollPrev ? 'opacity-100 hover:bg-white hover:scale-105' : 'opacity-50 cursor-not-allowed'
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="flex gap-3 items-center">
        {Array.from({ length: total }).map((_, index) => (
          <div
            key={index}
            className={cn(
              'transition-all ease-out duration-300 flex items-center justify-center hover:bg-primary cursor-pointer',
              activeIndex === index
                ? 'size-6 bg-primary rounded-full'
                : 'size-4 bg-primary/20 rounded-full hover:bg-primary/40'
            )}
            onClick={() => api?.scrollTo(index)}
          >
            <span className="size-3 rounded-full bg-white" />
          </div>
        ))}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'size-10 rounded-full border bg-white/80 backdrop-blur-sm transition-all duration-300',
          canScrollNext ? 'opacity-100 hover:bg-white hover:scale-105' : 'opacity-50 cursor-not-allowed'
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
      >
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  )
}

const cardSizeClassMap: Record<string, string> = {
  default: 'lg:basis-[70%]',
  large: 'lg:basis-[80%]',
  full: 'lg:basis-full',
}

function ProjectContainer({
  projects,
  autoplay = false,
  autoplayDelay = 3000,
  bgColor,
  cardSize = 'default',
}: {
  projects: (FeedbackType | ProjectItem)[]
  autoplay?: boolean
  autoplayDelay?: number
  bgColor?: string
  cardSize?: string
}) {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)

  const minSlidesForLoop = 3
  const needsDuplication = projects.length > 0 && projects.length < minSlidesForLoop
  const slides = needsDuplication
    ? [...projects, ...projects, ...projects].slice(0, Math.max(minSlidesForLoop, projects.length * 2))
    : projects
  const originalCount = projects.length

  React.useEffect(() => {
    if (!api) return

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const hasBg = bgColor && bgColor !== 'transparent'
  const cardSizeClass = cardSizeClassMap[cardSize] || cardSizeClassMap.default
  const autoplayPlugin = React.useRef(Autoplay({ delay: autoplayDelay, stopOnInteraction: false }))

  return (
    <div className="container py-10">
      <Carousel
        className="w-full relative"
        opts={{
          loop: true,
          align: 'center',
          skipSnaps: false,
        }}
        plugins={autoplay ? [autoplayPlugin.current] : []}
        onMouseEnter={autoplay ? autoplayPlugin.current.stop : undefined}
        onMouseLeave={autoplay ? autoplayPlugin.current.reset : undefined}
        setApi={setApi}
      >
        <div
          className="hidden lg:block absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
          style={{
            background: hasBg
              ? `linear-gradient(to right, ${bgColor}, ${bgColor}cc, transparent)`
              : 'linear-gradient(to right, white, rgba(255,255,255,0.8), transparent)',
          }}
        />
        <div
          className="hidden lg:block absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none"
          style={{
            background: hasBg
              ? `linear-gradient(to left, ${bgColor}, ${bgColor}cc, transparent)`
              : 'linear-gradient(to left, white, rgba(255,255,255,0.8), transparent)',
          }}
        />
        <CarouselContent className="overflow-visible">
          {slides.map((project, index) => (
            <CarouselItem key={index} className={cn('basis-full', cardSizeClass)}>
              <CardProjectStudent project={project} isCenter={current === index} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPagination
          api={api}
          activeIndex={needsDuplication ? current % originalCount : current}
          total={originalCount}
        />
      </Carousel>
    </div>
  )
}

function ProjectSection({
  className,
  heading,
  description,
  headingColor,
  descriptionColor,
  bgColor = 'transparent',
  dataSource = 'api',
  pageSize = 6,
  projects: customProjects,
  cardSize = 'default',
  autoplay = false,
  autoplayDelay = 3000,
}: ProjectSectionProps) {
  const { text } = useLocale()

  // Determine which data to use
  // Ensure each custom project item has an id (editor-created items may lack one)
  const useCustomData = dataSource === 'custom' && customProjects?.length
  const displayProjects: (FeedbackType | ProjectItem)[] = useCustomData
    ? customProjects.map((p, i) => ({ ...p, id: p.id ?? i + 1 }))
    : defaultFeedbacks.slice(0, pageSize)

  return (
    <section
      className={cn('md:py-16 sm:py-8', className)}
      id="student-thoughts"
      style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
    >
      <PageHeader className="text-center mx-auto space-y-4">
        <PageHeaderHeading
          size="default"
          style={headingColor ? { color: headingColor } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(heading || text.MAIN.BODY.HEADING_4) }}
        />
        <PageHeaderDescription
          size="default"
          className="max-w-[60.875rem] mx-auto"
          style={descriptionColor ? { color: descriptionColor } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(description || text.MAIN.BODY.HEADING_5) }}
        />
      </PageHeader>
      <ProjectContainer
        projects={displayProjects}
        autoplay={autoplay}
        autoplayDelay={autoplayDelay}
        bgColor={bgColor}
        cardSize={cardSize}
      />
    </section>
  )
}

export default ProjectSection
